import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabaseClient'

const AdminPayments = () => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [filter, setFilter] = useState({
    status: '',
    product_type: '',
    customer_type: '',
    invoice_state: '',
    from_date: '',
    to_date: '',
  })
  const [togglingId, setTogglingId] = useState(null)
  const [invoiceNumberDraft, setInvoiceNumberDraft] = useState('')
  const [savingInvoiceNumber, setSavingInvoiceNumber] = useState(false)
  const [exporting, setExporting] = useState(false)

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

  useEffect(() => {
    fetchPayments()
  }, [filter])

  const fetchPayments = async () => {
    setLoading(true)
    setError('')

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        setError('Nie ste prihlásený')
        setLoading(false)
        return
      }

      // Build query params
      const params = new URLSearchParams()
      if (filter.status) params.append('status', filter.status)
      if (filter.product_type) params.append('product_type', filter.product_type)
      if (filter.customer_type) params.append('customer_type', filter.customer_type)
      if (filter.invoice_state) params.append('invoice_state', filter.invoice_state)
      if (filter.from_date) params.append('from_date', filter.from_date)
      if (filter.to_date) params.append('to_date', filter.to_date)

      const response = await fetch(`${BACKEND_URL}/api/admin/payments?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPayments(data.payments || [])
      } else {
        const errorData = await response.json()
        setError(errorData.detail || 'Chyba pri načítavaní platieb')
      }
    } catch (err) {
      setError('Chyba pri načítavaní platieb')
      console.error('Fetch payments error:', err)
    } finally {
      setLoading(false)
    }
  }

  const viewPaymentDetail = async (paymentId) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      const response = await fetch(`${BACKEND_URL}/api/admin/payments/${paymentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setSelectedPayment(data.payment)
        setInvoiceNumberDraft((data.payment?.metadata || {}).invoice_number || '')
      } else {
        setError('Chyba pri načítavaní detailu platby')
      }
    } catch (err) {
      setError('Chyba pri načítavaní detailu')
      console.error('Fetch payment detail error:', err)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('sk-SK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatAmount = (amount, currency) => {
    const amountEur = amount / 100
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'EUR'
    }).format(amountEur)
  }

  const getProductLabel = (payment) => {
    const metadata = payment.metadata || {}
    const productType = metadata.product_type || 'unknown'

    if (productType === 'financial_literacy_school_package') {
      return '🏫 Školský balík FG'
    } else if (metadata.product === 'program_access' || productType === 'program_access') {
      return '👤 Individuálny prístup'
    }
    return '❓ Neznámy produkt'
  }

  const getCustomerTypeLabel = (type) => {
    switch (type) {
      case 'individual': return 'Fyzická osoba'
      case 'company': return 'Firma / organizácia'
      case 'school': return 'Škola'
      default: return type || '—'
    }
  }

  // Resolution helpers reused across list + detail
  const resolvePayer = (payment) => {
    const md = payment.metadata || {}
    const billing = (md.billing_data && typeof md.billing_data === 'object') ? md.billing_data : {}
    const customer_type = md.customer_type || billing.customer_type
    const invoice_name = billing.invoice_name || md.invoice_name || md.school_name
    const billing_email = billing.billing_email || md.billing_email
    return { customer_type, invoice_name, billing_email }
  }

  const resolveDelivery = (payment) => {
    const md = payment.metadata || {}
    const raw = md.electronic_invoice_consent
    if (raw === undefined || raw === null) return null
    return raw === true || raw === 'true' || raw === 'True' ? 'electronic' : 'postal'
  }

  const isInvoiceIssued = (payment) => Boolean((payment.metadata || {}).invoice_issued)
  const getInvoiceNumber = (payment) => (payment.metadata || {}).invoice_number || ''

  const patchInvoice = async (paymentId, payload) => {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    const response = await fetch(`${BACKEND_URL}/api/admin/payments/${paymentId}/invoice-status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload)
    })
    if (!response.ok) {
      const e = await response.json().catch(() => ({}))
      throw new Error(e.detail || 'Chyba pri zmene stavu faktúry')
    }
    return response.json()
  }

  const applyMetadataLocally = (paymentId, metadata) => {
    setPayments(prev => prev.map(p => p.id === paymentId ? { ...p, metadata } : p))
    if (selectedPayment && selectedPayment.id === paymentId) {
      setSelectedPayment(prev => ({ ...prev, metadata }))
    }
  }

  const toggleInvoiceIssued = async (payment) => {
    setTogglingId(payment.id)
    setError('')
    try {
      const next = !isInvoiceIssued(payment)
      const data = await patchInvoice(payment.id, { invoice_issued: next })
      applyMetadataLocally(payment.id, data.metadata)
    } catch (err) {
      setError(err.message || 'Chyba pri zmene stavu faktúry')
    } finally {
      setTogglingId(null)
    }
  }

  const saveInvoiceNumber = async () => {
    if (!selectedPayment) return
    setSavingInvoiceNumber(true)
    setError('')
    try {
      const trimmed = invoiceNumberDraft.trim()
      const data = await patchInvoice(selectedPayment.id, { invoice_number: trimmed || null })
      applyMetadataLocally(selectedPayment.id, data.metadata)
    } catch (err) {
      setError(err.message || 'Chyba pri ukladaní čísla faktúry')
    } finally {
      setSavingInvoiceNumber(false)
    }
  }

  const markAsInvoicedWithNumber = async () => {
    if (!selectedPayment) return
    setSavingInvoiceNumber(true)
    setError('')
    try {
      const trimmed = invoiceNumberDraft.trim()
      const payload = { invoice_issued: true }
      if (trimmed) payload.invoice_number = trimmed
      const data = await patchInvoice(selectedPayment.id, payload)
      applyMetadataLocally(selectedPayment.id, data.metadata)
    } catch (err) {
      setError(err.message || 'Chyba pri označovaní faktúry')
    } finally {
      setSavingInvoiceNumber(false)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    setError('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const params = new URLSearchParams()
      if (filter.status) params.append('status', filter.status)
      if (filter.product_type) params.append('product_type', filter.product_type)
      if (filter.customer_type) params.append('customer_type', filter.customer_type)
      if (filter.invoice_state) params.append('invoice_state', filter.invoice_state)
      if (filter.from_date) params.append('from_date', filter.from_date)
      if (filter.to_date) params.append('to_date', filter.to_date)

      const response = await fetch(`${BACKEND_URL}/api/admin/payments/export.csv?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) {
        const e = await response.json().catch(() => ({}))
        throw new Error(e.detail || 'Chyba pri exporte')
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const fnMatch = (response.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/i)
      a.download = fnMatch ? fnMatch[1] : 'edumisia_payments.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.message || 'Chyba pri exporte CSV')
    } finally {
      setExporting(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    }
    const labels = {
      completed: 'Dokončená',
      pending: 'Čaká',
      failed: 'Zlyhala',
      refunded: 'Vrátená'
    }
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Platby</h1>
        <p className="text-gray-600">Prehľad platieb pre manuálne vystavovanie faktúr</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6" data-testid="payments-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stav</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-status"
            >
              <option value="">Všetky</option>
              <option value="completed">Dokončené</option>
              <option value="pending">Čakajúce</option>
              <option value="failed">Zlyhané</option>
              <option value="refunded">Vrátené</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Typ produktu</label>
            <select
              value={filter.product_type}
              onChange={(e) => setFilter({ ...filter, product_type: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-product-type"
            >
              <option value="">Všetky</option>
              <option value="financial_literacy_school_package">Školské balíky FG</option>
              <option value="program_access">Individuálny prístup</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Typ zákazníka</label>
            <select
              value={filter.customer_type}
              onChange={(e) => setFilter({ ...filter, customer_type: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-customer-type"
            >
              <option value="">Všetky</option>
              <option value="individual">Fyzická osoba</option>
              <option value="company">Firma / organizácia</option>
              <option value="school">Škola</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Stav faktúry</label>
            <select
              value={filter.invoice_state}
              onChange={(e) => setFilter({ ...filter, invoice_state: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-invoice-state"
            >
              <option value="">Všetky</option>
              <option value="pending">Nevystavené</option>
              <option value="issued">Vystavené</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Od dátumu</label>
            <input
              type="date"
              value={filter.from_date}
              onChange={(e) => setFilter({ ...filter, from_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-from-date"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Do dátumu</label>
            <input
              type="date"
              value={filter.to_date}
              onChange={(e) => setFilter({ ...filter, to_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="filter-to-date"
            />
          </div>
        </div>

        <div className="mt-3 flex justify-between items-center">
          <button
            onClick={() => setFilter({ status: '', product_type: '', customer_type: '', invoice_state: '', from_date: '', to_date: '' })}
            className="text-sm text-gray-600 hover:text-gray-800"
            data-testid="filter-clear"
          >
            Vyčistiť filtre
          </button>
          <div className="flex gap-2">
            <button
              onClick={exportCsv}
              disabled={exporting}
              className="px-4 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
              data-testid="export-csv"
            >
              {exporting ? 'Exportujem...' : '⬇ Export CSV'}
            </button>
            <button
              onClick={fetchPayments}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              data-testid="filter-refresh"
            >
              Obnoviť
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Načítavam platby...</p>
        </div>
      ) : (
        <>
          {/* Payments Table */}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dátum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produkt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ zákazníka</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plátca</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fakturačný email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suma</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav platby</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doručenie</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Faktúra</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akcie</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                      Žiadne platby nenájdené
                    </td>
                  </tr>
                ) : (
                  payments.map((payment) => {
                    const payer = resolvePayer(payment)
                    const delivery = resolveDelivery(payment)
                    const issued = isInvoiceIssued(payment)
                    return (
                      <tr key={payment.id} className={`hover:bg-gray-50 ${issued ? 'opacity-80' : ''}`} data-testid={`payment-row-${payment.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                          {formatDate(payment.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          {getProductLabel(payment)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">
                          {payer.customer_type ? getCustomerTypeLabel(payer.customer_type) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-900 max-w-[180px] truncate" title={payer.invoice_name || ''}>
                          {payer.invoice_name || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700 max-w-[200px] truncate" title={payer.billing_email || ''}>
                          {payer.billing_email || payment.user_email || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                          {formatAmount(payment.amount, payment.currency)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          {getStatusBadge(payment.status)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          {delivery === 'electronic' && (
                            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">📧 E-mail</span>
                          )}
                          {delivery === 'postal' && (
                            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">📮 Pošta</span>
                          )}
                          {delivery === null && (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">
                          {issued ? (
                            <div data-testid={`invoice-issued-${payment.id}`}>
                              <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-semibold">
                                ✓ Vystavená
                              </span>
                              {getInvoiceNumber(payment) && (
                                <div className="mt-1 text-xs text-gray-700 font-mono" data-testid={`invoice-number-${payment.id}`}>
                                  č. {getInvoiceNumber(payment)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium" data-testid={`invoice-pending-${payment.id}`}>
                              ⏳ Čaká
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs space-y-1">
                          <button
                            onClick={() => viewPaymentDetail(payment.id)}
                            className="block text-blue-600 hover:text-blue-800 font-medium"
                            data-testid={`detail-${payment.id}`}
                          >
                            Detail
                          </button>
                          {payment.status === 'completed' && (
                            <button
                              onClick={() => toggleInvoiceIssued(payment)}
                              disabled={togglingId === payment.id}
                              className={`block font-medium disabled:opacity-50 ${issued ? 'text-amber-600 hover:text-amber-800' : 'text-green-600 hover:text-green-800'}`}
                              data-testid={`toggle-invoice-${payment.id}`}
                            >
                              {togglingId === payment.id ? '…' : (issued ? 'Zrušiť' : 'Označiť vystavené')}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Payment Detail Modal */}
          {selectedPayment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Detail platby</h2>
                    <button
                      onClick={() => setSelectedPayment(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Payment ID</p>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{selectedPayment.id}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Stripe Payment ID</p>
                        <p className="mt-1 text-sm text-gray-900 font-mono">
                          {selectedPayment.stripe_payment_id || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Dátum vytvorenia</p>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedPayment.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Dátum platby</p>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedPayment.paid_at)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Suma</p>
                        <p className="mt-1 text-sm font-bold text-gray-900">
                          {formatAmount(selectedPayment.amount, selectedPayment.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Stav</p>
                        <p className="mt-1">{getStatusBadge(selectedPayment.status)}</p>
                      </div>
                    </div>

                    {/* Metadata / Billing Details */}
                    {selectedPayment.metadata && (() => {
                      const md = selectedPayment.metadata || {}
                      const billing = (md.billing_data && typeof md.billing_data === 'object') ? md.billing_data : {}

                      // Resolve fields with priority: nested billing_data → flattened metadata → school fields
                      const customerType = md.customer_type || billing.customer_type
                      const invoiceName = billing.invoice_name || md.invoice_name || md.school_name
                      const billingEmail = billing.billing_email || md.billing_email
                      const phone = billing.phone || md.phone
                      const street = billing.street_address || md.street_address
                      const postalCode = billing.postal_code || md.postal_code
                      const city = billing.city || md.city
                      const country = billing.country || md.country
                      const ico = billing.ico || md.ico
                      const dic = billing.dic || md.dic
                      const icDph = billing.ic_dph || md.ic_dph

                      // Phase 2 invoice delivery preference
                      const consentRaw = md.electronic_invoice_consent
                      const hasConsentField = consentRaw !== undefined && consentRaw !== null
                      const consent = consentRaw === true || consentRaw === 'true' || consentRaw === 'True'
                      const postal = (md.postal_invoice_address && typeof md.postal_invoice_address === 'object')
                        ? md.postal_invoice_address : null

                      // School-specific extras
                      const officialEmail = md.official_school_email
                      const contactName = md.contact_name
                      const note = md.note

                      const Row = ({ label, value, className = '' }) => (
                        <div className={className}>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
                          <p className="mt-1 text-sm text-gray-900">{value || '—'}</p>
                        </div>
                      )

                      return (
                        <div className="border-t pt-6" data-testid="billing-section">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fakturačné údaje</h3>

                          {/* Customer type + name */}
                          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Row label="Typ zákazníka" value={getCustomerTypeLabel(customerType)} />
                              <Row label={customerType === 'company' ? 'Názov firmy' : (customerType === 'school' ? 'Názov školy' : 'Meno na faktúre')} value={invoiceName} />
                            </div>

                            {/* Contact info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Row label="Fakturačný email" value={billingEmail} />
                              <Row label="Telefón" value={phone} />
                            </div>

                            {/* School-only extras */}
                            {(officialEmail || contactName) && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {officialEmail && <Row label="Oficiálny školský e-mail" value={officialEmail} />}
                                {contactName && <Row label="Kontaktná osoba" value={contactName} />}
                              </div>
                            )}

                            {/* Address */}
                            {(street || postalCode || city || country) && (
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Fakturačná adresa</p>
                                <p className="text-sm text-gray-900 leading-relaxed">
                                  {street && <>{street}<br /></>}
                                  {(postalCode || city) && <>{postalCode} {city}<br /></>}
                                  {country}
                                </p>
                              </div>
                            )}

                            {/* Company / school IDs */}
                            {(ico || dic || icDph) && (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-gray-200">
                                <Row label="IČO" value={ico} />
                                <Row label="DIČ" value={dic} />
                                <Row label="IČ DPH" value={icDph} />
                              </div>
                            )}

                            {note && (
                              <Row label="Poznámka" value={note} />
                            )}
                          </div>

                          {/* Phase 2: Invoice delivery preference */}
                          {hasConsentField && (
                            <div className={`mt-4 rounded-lg p-4 ${consent ? 'bg-blue-50 border border-blue-200' : 'bg-amber-50 border border-amber-300'}`} data-testid="invoice-delivery-block">
                              <div className="flex items-start gap-2 mb-2">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${consent ? 'bg-blue-600 text-white' : 'bg-amber-600 text-white'}`}>
                                  {consent ? 'Elektronická faktúra' : 'Poštová faktúra'}
                                </span>
                                <p className="text-sm text-gray-700">
                                  {consent
                                    ? 'Zákazník súhlasí so zaslaním faktúry e-mailom.'
                                    : 'Zákazník požaduje doručenie faktúry poštou na uvedenú adresu.'}
                                </p>
                              </div>

                              {!consent && postal && (
                                <div className="mt-3 bg-white rounded p-3 space-y-2 border border-amber-200" data-testid="postal-address-block">
                                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Poštová adresa pre faktúru</p>
                                  <p className="text-sm text-gray-900 leading-relaxed">
                                    {postal.name && <><strong>{postal.name}</strong><br /></>}
                                    {postal.street && <>{postal.street}<br /></>}
                                    {(postal.postal_code || postal.city) && <>{postal.postal_code} {postal.city}<br /></>}
                                    {postal.country}
                                  </p>
                                </div>
                              )}

                              {!consent && !postal && (
                                <p className="text-xs text-amber-800 italic mt-2">
                                  ⚠️ Súhlas vypnutý, ale poštová adresa nie je v metadata uvedená — skontrolujte raw JSON.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })()}

                    {/* Phase 3+4: Invoice work-state action inside detail modal */}
                    {selectedPayment.status === 'completed' && (() => {
                      const issued = isInvoiceIssued(selectedPayment)
                      const md = selectedPayment.metadata || {}
                      const currentNumber = md.invoice_number || ''
                      const draftDirty = invoiceNumberDraft.trim() !== currentNumber.trim()
                      return (
                        <div className="border-t pt-6" data-testid="invoice-state-block">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">Faktúra (manuálne spracovanie)</h3>

                          <div className={`rounded-lg p-4 ${issued ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                            {/* Status header */}
                            <div className="flex items-start justify-between gap-4 mb-4">
                              <div>
                                {issued ? (
                                  <>
                                    <p className="text-sm font-semibold text-green-800">✓ Faktúra bola vystavená</p>
                                    {md.invoice_issued_at && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {formatDate(md.invoice_issued_at)}
                                        {md.invoice_issued_by ? ` · ${md.invoice_issued_by}` : ''}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-sm font-medium text-gray-700">⏳ Faktúra ešte nebola vystavená</p>
                                )}
                              </div>
                              {issued && (
                                <button
                                  onClick={() => toggleInvoiceIssued(selectedPayment)}
                                  disabled={togglingId === selectedPayment.id}
                                  className="px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-50"
                                  data-testid="modal-toggle-invoice"
                                >
                                  {togglingId === selectedPayment.id ? '…' : 'Zrušiť označenie'}
                                </button>
                              )}
                            </div>

                            {/* Invoice number editor */}
                            <div className="space-y-2">
                              <label className="block text-xs font-medium uppercase tracking-wide text-gray-600">
                                Číslo faktúry
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={invoiceNumberDraft}
                                  onChange={(e) => setInvoiceNumberDraft(e.target.value)}
                                  placeholder="napr. 2026-001"
                                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  data-testid="modal-invoice-number-input"
                                />
                                {issued ? (
                                  <button
                                    onClick={saveInvoiceNumber}
                                    disabled={savingInvoiceNumber || !draftDirty}
                                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                                    data-testid="modal-save-invoice-number"
                                  >
                                    {savingInvoiceNumber ? '…' : 'Uložiť'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={markAsInvoicedWithNumber}
                                    disabled={savingInvoiceNumber}
                                    className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                                    data-testid="modal-mark-invoiced"
                                  >
                                    {savingInvoiceNumber ? '…' : 'Označiť ako vystavenú'}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {issued
                                  ? 'Môžete upraviť číslo faktúry alebo zrušiť označenie.'
                                  : 'Voliteľne zadajte číslo faktúry. Ak ho necháte prázdne, faktúru môžete označiť ako vystavenú aj bez čísla.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {/* Raw Metadata for debugging */}
                    <details className="border-t pt-4">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        Zobraziť kompletné metadata (JSON)
                      </summary>
                      <pre className="mt-2 text-xs bg-gray-100 p-4 rounded overflow-x-auto">
                        {JSON.stringify(selectedPayment.metadata, null, 2)}
                      </pre>
                    </details>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setSelectedPayment(null)}
                      className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
                    >
                      Zavrieť
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AdminPayments
